import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Search, Users, Shield, UserCheck, Loader2, Edit, Trash2, Key, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { PhoneWithWhatsApp } from '../components/WhatsAppLink';

const RUOLI = [
  { value: 'admin_chiesa', label: 'Admin Chiesa', icon: Shield },
  { value: 'volontario', label: 'Volontario', icon: UserCheck },
];

const UserForm = ({ utente, chiese, onSave, onClose }) => {
  const [formData, setFormData] = useState(utente || {
    nome: '', cognome: '', email: '', password: '',
    ruolo: 'volontario', chiesa_id: '', telefono: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const processedData = { ...formData, telefono: formData.telefono || null };
      await onSave(processedData);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Nome *</Label>
          <Input value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} required data-testid="utente-nome-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Cognome *</Label>
          <Input value={formData.cognome} onChange={(e) => setFormData({ ...formData, cognome: e.target.value })} required />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Email *</Label>
        <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="utente-email-input" />
      </div>
      {!utente && (
        <div className="space-y-1.5">
          <Label>Password *</Label>
          <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required />
        </div>
      )}
      <div className="space-y-1.5">
        <Label>Telefono</Label>
        <Input type="tel" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Ruolo *</Label>
          <Select value={formData.ruolo} onValueChange={(v) => setFormData({ ...formData, ruolo: v })}>
            <SelectTrigger data-testid="utente-ruolo-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RUOLI.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Chiesa *</Label>
          <Select value={formData.chiesa_id} onValueChange={(v) => setFormData({ ...formData, chiesa_id: v })}>
            <SelectTrigger data-testid="utente-chiesa-select"><SelectValue placeholder="Seleziona chiesa" /></SelectTrigger>
            <SelectContent>
              {chiese.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2 sticky bottom-0 bg-background pb-1">
        <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">Annulla</Button>
        <Button type="submit" disabled={loading} className="w-full sm:w-auto" data-testid="utente-submit-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {utente ? 'Aggiorna' : 'Crea'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const ChangePasswordDialog = ({ userId, onClose, API }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/users/${userId}/change-password`, { new_password: password });
      toast.success('Password aggiornata');
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Nuova Password *</Label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="new-password-input" />
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">Annulla</Button>
        <Button type="submit" disabled={loading} className="w-full sm:w-auto" data-testid="change-password-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Cambia Password
        </Button>
      </DialogFooter>
    </form>
  );
};

const ReassignDialog = ({ userId, chiese, onClose, API, onRefresh }) => {
  const [chiesaId, setChiesaId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/users/${userId}/reassign-chiesa`, { new_chiesa_id: chiesaId });
      toast.success('Utente riassegnato');
      onRefresh();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Nuova Chiesa *</Label>
        <Select value={chiesaId} onValueChange={setChiesaId}>
          <SelectTrigger data-testid="reassign-chiesa-select"><SelectValue placeholder="Seleziona chiesa" /></SelectTrigger>
          <SelectContent>
            {chiese.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">Annulla</Button>
        <Button type="submit" disabled={loading || !chiesaId} className="w-full sm:w-auto" data-testid="reassign-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Riassegna
        </Button>
      </DialogFooter>
    </form>
  );
};

const UtenteMobileCard = ({ u, isSuperAdmin, isAdmin, openEdit, handleDelete, setPasswordUserId, setReassignUserId }) => (
  <div className="mobile-card-item" data-testid={`utente-card-${u.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{u.nome} {u.cognome}</span>
          <Badge variant={u.ruolo === 'super_admin' ? 'default' : u.ruolo === 'admin_chiesa' ? 'secondary' : 'outline'} className="text-[10px] px-1.5 py-0">
            {u.ruolo?.replace('_', ' ')}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 truncate">{u.email}</p>
        {u.telefono && (
          <div className="mt-1" onClick={(e) => e.stopPropagation()}>
            <PhoneWithWhatsApp phone={u.telefono} />
          </div>
        )}
        {u.chiesa_nome && <p className="text-[10px] text-muted-foreground mt-0.5">{u.chiesa_nome}</p>}
      </div>
      <div className="flex flex-col gap-1 flex-shrink-0">
        <div className="flex items-center gap-1">
          {isAdmin() && (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-utente-${u.id}`}><Edit className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPasswordUserId(u.id)} data-testid={`pwd-utente-${u.id}`}><Key className="w-3.5 h-3.5" /></Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isSuperAdmin() && (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setReassignUserId(u.id)}><RefreshCw className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(u.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
);

export const UtentiPage = () => {
  const { API, isAdmin, isSuperAdmin } = useAuth();
  const [utenti, setUtenti] = useState([]);
  const [chiese, setChiese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUtente, setEditingUtente] = useState(null);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [reassignUserId, setReassignUserId] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [utentiRes, chieseRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/chiese`)
      ]);
      setUtenti(utentiRes.data);
      setChiese(chieseRes.data);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    if (editingUtente) {
      await axios.put(`${API}/users/${editingUtente.id}`, data);
      toast.success('Utente aggiornato');
    } else {
      await axios.post(`${API}/users`, data);
      toast.success('Utente creato');
    }
    fetchData();
    setEditingUtente(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo utente?")) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success('Utente eliminato');
      fetchData();
    } catch (err) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const openEdit = (u) => { setEditingUtente(u); setDialogOpen(true); };
  const openCreate = () => { setEditingUtente(null); setDialogOpen(true); };

  const chieseMap = {};
  chiese.forEach(c => { chieseMap[c.id] = c.nome; });

  const filteredUtenti = utenti.filter(u =>
    `${u.nome} ${u.cognome} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  ).map(u => ({ ...u, chiesa_nome: chieseMap[u.chiesa_id] || null }));

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="utenti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Utenti</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Gestisci gli utenti della piattaforma</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <Button onClick={openCreate} className="w-full sm:w-auto" data-testid="add-utente-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Utente
            </Button>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingUtente ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle>
              </DialogHeader>
              <UserForm utente={editingUtente} chiese={chiese} onSave={handleSave} onClose={() => setDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input placeholder="Cerca utente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="utenti-search-input" />
      </div>

      {/* Content */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 sm:p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : filteredUtenti.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">Nessun utente trovato</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Ruolo</TableHead>
                      <TableHead className="hidden lg:table-cell">Chiesa</TableHead>
                      <TableHead className="hidden lg:table-cell">Telefono</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUtenti.map((u) => (
                      <TableRow key={u.id} data-testid={`utente-row-${u.id}`}>
                        <TableCell><span className="font-medium">{u.nome} {u.cognome}</span></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                        <TableCell>
                          <Badge variant={u.ruolo === 'super_admin' ? 'default' : u.ruolo === 'admin_chiesa' ? 'secondary' : 'outline'}>
                            {u.ruolo?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">{u.chiesa_nome || '-'}</TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {u.telefono ? <PhoneWithWhatsApp phone={u.telefono} /> : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {isAdmin() && (
                              <>
                                <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-utente-${u.id}`}><Edit className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => setPasswordUserId(u.id)} title="Cambia password"><Key className="w-4 h-4" /></Button>
                              </>
                            )}
                            {isSuperAdmin() && (
                              <>
                                <Button variant="ghost" size="icon" onClick={() => setReassignUserId(u.id)} title="Riassegna chiesa"><RefreshCw className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(u.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card List */}
              <div className="md:hidden">
                {filteredUtenti.map((u) => (
                  <UtenteMobileCard key={u.id} u={u} isSuperAdmin={isSuperAdmin} isAdmin={isAdmin} openEdit={openEdit}
                    handleDelete={handleDelete} setPasswordUserId={setPasswordUserId} setReassignUserId={setReassignUserId} />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Change Password Dialog */}
      <Dialog open={!!passwordUserId} onOpenChange={(open) => !open && setPasswordUserId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambia Password</DialogTitle></DialogHeader>
          {passwordUserId && <ChangePasswordDialog userId={passwordUserId} onClose={() => setPasswordUserId(null)} API={API} />}
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={!!reassignUserId} onOpenChange={(open) => !open && setReassignUserId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Riassegna Chiesa</DialogTitle></DialogHeader>
          {reassignUserId && <ReassignDialog userId={reassignUserId} chiese={chiese} onClose={() => setReassignUserId(null)} API={API} onRefresh={fetchData} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UtentiPage;
